// Shopify owns the banner, stored choices, modal and focus restoration.
// https://shopify.dev/docs/api/customer-privacy#showing-the-preferences-modal
(() => {
  if (customElements.get('cookie-preferences')) return;

  customElements.define('cookie-preferences', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button || this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.setAvailable(false);
      // LC03 visitor-preview audit: Shopify exports showPreferences here, but its
      // native configuration request fails. Do not advertise an unusable control.
      // Merchant previews on the storefront domain still use the native API.
      if (/(^|\.)shopifypreview\.com$/i.test(location.hostname)) return;

      const ready = () => !this.failed &&
        typeof window.privacyBanner?.showPreferences === 'function';
      const sync = () => {
        const available = ready();
        this.setAvailable(available);
        if (available) clearInterval(this.readinessTimer);
        return available;
      };
      const observeReadiness = () => {
        clearInterval(this.readinessTimer);
        if (sync() || this.failed) return;
        let attempts = 0;
        // Also handle a late API assignment without a script load or Shopify event.
        this.readinessTimer = setInterval(() => {
          if (sync() || ++attempts >= 60) clearInterval(this.readinessTimer);
        }, 250);
      };

      this.button.addEventListener('click', async () => {
        if (this.opening || !sync()) return;
        const banner = window.privacyBanner;
        // Safari doesn't always focus a clicked button. Give Shopify a return target.
        this.button.focus({preventScroll: true});
        this.opening = true;
        this.button.setAttribute('aria-busy', 'true');
        const finished = () => {
          this.openObserver?.disconnect();
          clearTimeout(this.openTimer);
          this.opening = false;
          this.button.removeAttribute('aria-busy');
        };
        const failed = () => {
          finished();
          this.failed = true;
          if (document.activeElement === this.button) {
            this.closest('.footer-legal')?.querySelector('a')?.focus({preventScroll: true});
          }
          this.setAvailable(false);
        };
        const opened = () => {
          // The native script currently catches initialization errors internally;
          // its resolved promise alone does not prove that a modal was opened.
          const modal = document.getElementById('shopify-pc__prefs__dialog');
          if (!modal || !modal.getClientRects().length) return;
          finished();
        };
        this.openObserver = new MutationObserver(opened);
        this.openObserver.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open']});
        this.openTimer = setTimeout(failed, 10000);
        try {
          await banner.showPreferences();
          opened();
        } catch {
          failed();
        }
      }, {signal});

      // Observe availability only; never load/duplicate the banner or alter consent.
      document.addEventListener('load', event => {
        if (event.target instanceof HTMLScriptElement) observeReadiness();
      }, {capture: true, signal});
      document.addEventListener('shopifyCustomerPrivacyApiLoaded', observeReadiness, {signal});
      window.addEventListener('pageshow', observeReadiness, {signal});
      observeReadiness();
    }

    setAvailable(available) {
      this.button.hidden = !available;
      const row = this.closest('[data-privacy-only]');
      if (row) row.hidden = !available;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearInterval(this.readinessTimer);
      clearTimeout(this.openTimer);
      this.openObserver?.disconnect();
      this.opening = false;
      this.button?.removeAttribute('aria-busy');
    }
  });
})();
